import React from 'react'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Pill, type Tone } from './ui'

/**
 * One step of the work, as a card that opens to its form or its result. The numbered badge
 * reads done / current / upcoming at a glance; the pill says whose move it is.
 */
export const StepCard: React.FC<{
  index: number
  title: string
  state: 'done' | 'needs_you' | 'with_brand' | 'upcoming'
  status?: string
  date?: string
  open: boolean
  onToggle: () => void
  children?: React.ReactNode
}> = ({ index, title, state, status, date, open, onToggle, children }) => {
  const tone: Tone =
    state === 'done'
      ? 'green'
      : state === 'needs_you'
        ? 'amber'
        : state === 'with_brand'
          ? 'blue'
          : 'grey'
  const expandable = !!children
  return (
    <section
      className={cn(
        'rounded-2xl border bg-card shadow-sm',
        state === 'needs_you' ? 'border-[#C9E9A6]' : 'border-border',
      )}
    >
      <button
        type="button"
        onClick={expandable ? onToggle : undefined}
        aria-expanded={expandable ? open : undefined}
        className={cn(
          'flex w-full items-center gap-3 p-4 text-left',
          !expandable && 'cursor-default',
        )}
      >
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold',
            state === 'done'
              ? 'border-[#C0FD72] bg-[#C0FD72] text-[#0F172A]'
              : state === 'needs_you'
                ? 'border-[#C0FD72] bg-[#F7FEE7] text-[#2F5E06]'
                : state === 'with_brand'
                  ? 'border-[#BFD7FB] bg-[#EFF6FF] text-[#1D4ED8]'
                  : 'border-border text-muted-foreground',
          )}
        >
          {state === 'done' ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : index}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block truncate text-base font-bold',
              state === 'upcoming' ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            {title}
          </span>
          {date ? <span className="block text-xs text-muted-foreground">{date}</span> : null}
        </span>
        {status ? <Pill tone={tone}>{status}</Pill> : null}
        {expandable ? (
          open ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          )
        ) : null}
      </button>
      {expandable && open ? <div className="px-4 pb-4">{children}</div> : null}
    </section>
  )
}

export default StepCard
